<ng-container *ngIf="(isSheetDatasetSuccessful$ | async) === false; else datasetOverview">
  <mode-google-sheet-dataset-failure
    [spreadsheetUrl]="(googleSheetMetadata$ | async)?.spreadsheetUrl"
    [isSyncing$]="googleSheetSyncFacade.isSyncing$"
    (syncClicked)="onSyncClick()"
  ></mode-google-sheet-dataset-failure>
</ng-container>

<ng-template #datasetOverview>
  <div
    class="header-container"
    *ngIf="switchView$ | async as switchView"
    (dblclick)="toggleHeader($event)"
    data-test="data-view-header"
  >
    <nav mat-tab-nav-bar>
      <a
        mat-tab-link
        *ngFor="let tab of tabs"
        (click)="selectTab(tab.title)"
        [active]="activeTab === tab.title"
        [attr.data-test]="tab.title.toLowerCase() + '-tab'"
      >
        {{ tab.title }}
        <capra-icon *ngIf="tab.warn" icon="icon-warning"></capra-icon>
      </a>
    </nav>
    <div class="action-container">
      <div *ngIf="activeTab === tabTypes.Fields">
        <capra-search-input
          class="filter-form"
          size="mini"
          placeholder="Search for a field..."
          [text]="fieldFilter"
          [autofocus]="false"
          (textChange)="handleFieldFilterChanged($event)"
        ></capra-search-input>
        <capra-button
          *ngIf="!hideCalcFields"
          class="button"
          (click)="addCalculation(switchView.name, switchView.sourceType)"
          data-test="new-calc-field-button"
          text="New field"
          size="small"
          variant="secondary"
          color="neutral"
          icon="illo-mini-cal-field-add"
        >
        </capra-button>
      </div>
      <div *ngIf="activeTab === tabTypes.Data">
        <capra-button
          *ngIf="
            showRefreshBtn &&
            (isNotebookCellDataset$ | async) === false &&
            (switchViewToken$ | async) as switchViewToken
          "
          text="Refresh"
          size="small"
          variant="primary"
          icon="icon-data-refresh"
          (click)="dataViewFacade.datasetRefreshButtonClicked(reportId, switchViewToken)"
          data-test="data-overview-refresh-dataset"
        ></capra-button>
        <button
          *ngIf="
            switchView.sourceType === sourceTypeQuery && !hideQueryLink && (isNotebookCellDataset$ | async) === false
          "
          modeClipboard
          [clipboardText]="datasetWebLink"
          class="copy-link-button"
          capraTooltip
          tooltipContent="Copy Query link"
          tooltipPlacement="bottom"
          data-test="copy-query-link-button"
        >
          <capra-icon icon="icon-link"></capra-icon>
        </button>
        <div
          *ngIf="showWarningMsg$ | async"
          class="warning-tooltip"
          capraTooltip
          [tooltipContent]="googleSyncWarningText"
          [tooltipContentIsHtml]="true"
          tooltipPlacement="bottom"
        >
          <capra-status-icon class="status-icon" status="warning"></capra-status-icon>
          <span class="warning-copy">1 warning</span>
        </div>
        <mode-query-dependencies
          *ngIf="(isSheetDataset$ | async) && (datasetImportCount$ | async) && showQueryDependenciesBtn"
          class="action"
          [reportsCount]="datasetImportCount$ | async"
          [datasetsReportType]="true"
        ></mode-query-dependencies>
        <mode-dataset-sync-button
          *ngIf="(isSheetDataset$ | async) && showSyncBtn"
          class="action"
          [isSheetsEnabled]="googleSheetsEnabled$ | async"
          [lastSynced]="lastSynced"
          [isSyncing]="isGoogleSheetSyncing$ | async"
          (syncClicked)="onSyncClick()"
        ></mode-dataset-sync-button>
        <a
          *ngIf="(organizationRestricted$ | async) === false && (switchViewToken$ | async) as switchViewToken"
          href="{{ csvUrl$ | async }}"
          (click)="trackExportClick($event)"
          target="_self"
          download
        >
          <capra-button
            icon="icon-control-export"
            variant="secondary"
            color="neutral"
            size="small"
            text="Export"
          ></capra-button>
        </a>
        <mode-copy-dataset-button
          *ngIf="(organizationRestricted$ | async) === false && (switchViewToken$ | async) as switchViewToken"
          [queryToken]="queryToken"
          [rowsCount]="rowCount$ | async"
          [datasetSizeBytes]="dataViewFacade.getBytesSizeBySwitchViewToken(switchViewToken) | async"
          [columns]="metaColumns$ | async"
          [jsonToCopy]="jsonUrl$ | async"
        ></mode-copy-dataset-button>
        <capra-button
          *ngIf="enablePublishCTAForExportedDataset"
          data-test="publish-exported-dataset"
          text="Publish"
          size="small"
          variant="primary"
          capraTooltip
          tooltipContent="Publish this dataset to import it as a table in the data workspace"
          tooltipPlacement="bottom-end"
          (clicked)="publishExportedDataset()"
        ></capra-button>
      </div>
      <div
        *ngIf="
          activeTab === tabTypes.Source &&
          (isSheetDataset$ | async) === false &&
          (isNotebookCellDataset$ | async) === false
        "
      >
        <button mat-button [matMenuTriggerFor]="rawQuery" class="sql-display">
          <span>Display</span>
          {{ isRawQuery ? 'Raw SQL' : 'Rendered SQL' }}
          <capra-icon icon="icon-caret-down"></capra-icon>
        </button>
        <mat-menu #rawQuery="matMenu" class="sql-display-menu">
          <button (click)="setIsRawQuery(true)" class="menu__item" [ngClass]="{ selected: isRawQuery }" mat-menu-item>
            <span class="indicator"></span>Raw SQL
          </button>
          <button (click)="setIsRawQuery(false)" class="menu__item" [ngClass]="{ selected: !isRawQuery }" mat-menu-item>
            <span class="indicator"></span>Rendered SQL
          </button>
        </mat-menu>
        <capra-button
          class="button"
          text="Copy SQL to clipboard"
          size="small"
          variant="secondary"
          color="neutral"
          modeClipboard
          [clipboardText]="(isRawQuery ? rawQuery$ : renderedQuery$) | async"
        >
        </capra-button>
      </div>
    </div>
  </div>
</ng-template>

<mode-status-banner
  class="data-persist-msg"
  *ngIf="(showPersistQueryMsg$ | async) || (isGoogleSheetSyncing$ | async)"
  status="info"
  show-icon="true"
  layout="full-width"
  accent="blue"
>
  <span>We're fetching new data. Feel free to continue using the previous results while you wait</span>
</mode-status-banner>
<div class="tabs-container">
  <div *ngFor="let tab of customTabs" [class.show]="activeTab === tab.modeDatasetOverviewTab.title" class="tab-content">
    <ng-template [ngTemplateOutlet]="tab.templateRef"></ng-template>
  </div>
  <div [class.show]="activeTab === tabTypes.Fields" class="tab-content">
    <mode-switch-view-fields
      *ngIf="switchView$ | async as switchView"
      [switchView]="switchView"
      [totalRecords]="rowCount$ | async"
      [size]="formattedBytesSize$ | async"
      [isImportedDataset]="switchView.sourceType === sourceTypeSwitchView"
      [showFieldDescriptionColumn]="showFieldDescriptionColumn"
      [disableCalcFieldsManagement]="hideCalcFields"
      [fieldFilter]="fieldFilter"
      [fieldDescReadOnly]="fieldDescReadOnly"
      (editCalcField)="onEditCalcField($event, switchView)"
      (viewCalcField)="onViewCalcField($event, switchView)"
      (duplicateCalcField)="onDuplicateCalcField($event, switchView)"
      (removeCalcField)="onRemoveCalcField($event, switchView)"
      (createSwitchAttr)="createSwitchAttr($event)"
      (updateSwitchAttr)="updateSwitchAttr($event)"
      (fieldDescEditToggled)="fieldDescriptionEditMode$.next($event)"
    ></mode-switch-view-fields>
  </div>
  <div [class.show]="activeTab === tabTypes.Data" class="tab-content">
    <ng-container *ngIf="(customDataTab$ | async) === null">
      <mode-flamingo-grid-ng
        *ngIf="(rowCount$ | async) > 0; else noDataToView"
        [csvLink]="csvUrl$ | async"
        [executionTime]="executionTime$ | async"
        [isFallbackData]="isFallbackData$ | async"
        [lastSuccessfulLabel]="(isSheetDataset$ | async) ? 'Synced' : 'Run'"
        [lastSuccessfulRunAt]="lastSuccessfulRunAt$ | async"
        [queryToken]="queryToken"
        [size]="formattedBytesSize$ | async"
      ></mode-flamingo-grid-ng>

      <ng-template #noDataToView>
        <ng-content
          *ngIf="(isSheetDatasetSuccessful$ | async) || (isExportedDatasetSuccessful$ | async)"
          select="[noDataToView]"
        ></ng-content>
      </ng-template>
    </ng-container>
  </div>
  <div class="tab-content tab-sql" [class.show]="activeTab === tabTypes.Source">
    <mode-dataview-query
      *ngIf="rawQuery$ | async"
      [rawQuery]="rawQuery$ | async"
      [renderedQuery]="renderedQuery$ | async"
      [isRawQuery]="isRawQuery"
      [connectionName]="connectionName"
      [connectionType]="connectionType"
    ></mode-dataview-query>

    <ng-container *ngIf="isSheetDataset$ | async">
      <mode-metadata-summary
        *ngIf="googleSheetMetadata$ | async as metadata"
        [spreadsheetName]="metadata.spreadsheetName"
        [worksheetName]="metadata.worksheetName"
        [spreadsheetUrl]="metadata.spreadsheetUrl"
        [worksheetRange]="metadata.worksheetRange"
        [ownedBy]="metadata.ownedBy"
        [connectedBy]="metadata.connectedBy"
        [lastSynced]="metadata.lastSynced"
        [lastSyncedBy]="metadata.lastSyncedBy"
      ></mode-metadata-summary>
    </ng-container>
    <ng-container *ngIf="enableNotebookExportDatasets && (isNotebookCellDataset$ | async)">
      <mode-export-dataset-view [exportedDatasetSource]="exportedDatasetSource$ | async"></mode-export-dataset-view>
    </ng-container>
  </div>
</div>
